export const DATABASE_COURSES = [
  {
    id: 'advanced-sql-analytics',
    title: 'Advanced SQL for Analytics',
    description: 'Master advanced SQL techniques for data analysis, window functions, and query optimization.',
    level: 'intermediate',
    totalDuration: '8-10 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['SQL fundamentals', 'Basic database concepts', 'Data analysis basics'],
    learningOutcomes: [
      'Master advanced SQL functions and techniques',
      'Optimize complex queries for performance',
      'Implement analytical window functions',
      'Design efficient database schemas',
      'Handle large-scale data processing',
      'Create sophisticated data reports'
    ],
    modules: [
      {
        id: 'advanced-sql-techniques',
        title: 'Advanced SQL Techniques',
        description: 'Master window functions, CTEs, and advanced analytical queries',
        duration: '120 minutes',
        difficulty: 'intermediate',
        objectives: [
          'Use window functions for advanced analytics',
          'Implement Common Table Expressions (CTEs)',
          'Optimize query performance',
          'Handle complex data transformations'
        ],
        content: `# Advanced SQL Techniques

## Window Functions

Window functions perform calculations across a set of table rows related to the current row.

### Basic Window Functions
\`\`\`sql
-- Row numbering and ranking
SELECT 
    customer_id,
    order_date,
    total_amount,
    ROW_NUMBER() OVER (PARTITION BY customer_id ORDER BY order_date) as order_sequence,
    RANK() OVER (ORDER BY total_amount DESC) as amount_rank,
    DENSE_RANK() OVER (ORDER BY total_amount DESC) as dense_rank,
    NTILE(4) OVER (ORDER BY total_amount) as quartile
FROM orders;

-- Lag and Lead functions
SELECT 
    customer_id,
    order_date,
    total_amount,
    LAG(total_amount) OVER (PARTITION BY customer_id ORDER BY order_date) as prev_order_amount,
    LEAD(order_date) OVER (PARTITION BY customer_id ORDER BY order_date) as next_order_date,
    total_amount - LAG(total_amount) OVER (PARTITION BY customer_id ORDER BY order_date) as amount_change
FROM orders;
\`\`\`

### Advanced Aggregations
\`\`\`sql
-- Running totals and moving averages
SELECT 
    order_date,
    daily_revenue,
    SUM(daily_revenue) OVER (
        ORDER BY order_date 
        ROWS BETWEEN UNBOUNDED PRECEDING AND CURRENT ROW
    ) as running_total,
    AVG(daily_revenue) OVER (
        ORDER BY order_date 
        ROWS BETWEEN 6 PRECEDING AND CURRENT ROW
    ) as moving_avg_7_days,
    SUM(daily_revenue) OVER (
        ORDER BY order_date 
        ROWS BETWEEN 29 PRECEDING AND CURRENT ROW
    ) as rolling_30_day_sum
FROM daily_sales;

-- Percentiles and statistical functions
SELECT 
    product_category,
    price,
    PERCENT_RANK() OVER (PARTITION BY product_category ORDER BY price) as price_percentile,
    CUME_DIST() OVER (PARTITION BY product_category ORDER BY price) as cumulative_distribution,
    PERCENTILE_CONT(0.5) WITHIN GROUP (ORDER BY price) OVER (PARTITION BY product_category) as median_price
FROM products;
\`\`\`

## Common Table Expressions (CTEs)

### Recursive CTEs
\`\`\`sql
-- Hierarchical data processing
WITH RECURSIVE employee_hierarchy AS (
    -- Base case: top-level managers
    SELECT 
        employee_id,
        name,
        manager_id,
        1 as level,
        CAST(name AS VARCHAR(1000)) as hierarchy_path
    FROM employees 
    WHERE manager_id IS NULL
    
    UNION ALL
    
    -- Recursive case: employees with managers
    SELECT 
        e.employee_id,
        e.name,
        e.manager_id,
        eh.level + 1,
        CONCAT(eh.hierarchy_path, ' -> ', e.name)
    FROM employees e
    INNER JOIN employee_hierarchy eh ON e.manager_id = eh.employee_id
)
SELECT * FROM employee_hierarchy ORDER BY level, hierarchy_path;

-- Time series generation
WITH RECURSIVE date_series AS (
    SELECT DATE('2024-01-01') as date_value
    UNION ALL
    SELECT date_value + INTERVAL 1 DAY
    FROM date_series
    WHERE date_value < DATE('2024-12-31')
)
SELECT 
    ds.date_value,
    COALESCE(s.revenue, 0) as daily_revenue
FROM date_series ds
LEFT JOIN sales s ON ds.date_value = s.sale_date
ORDER BY ds.date_value;
\`\`\`

### Complex Analytics with CTEs
\`\`\`sql
-- Customer cohort analysis
WITH first_purchases AS (
    SELECT 
        customer_id,
        MIN(order_date) as first_purchase_date,
        DATE_TRUNC('month', MIN(order_date)) as cohort_month
    FROM orders
    GROUP BY customer_id
),
customer_activities AS (
    SELECT 
        fp.customer_id,
        fp.cohort_month,
        DATE_TRUNC('month', o.order_date) as activity_month,
        EXTRACT(MONTH FROM AGE(o.order_date, fp.first_purchase_date)) as period_number
    FROM first_purchases fp
    JOIN orders o ON fp.customer_id = o.customer_id
),
cohort_data AS (
    SELECT 
        cohort_month,
        period_number,
        COUNT(DISTINCT customer_id) as customers
    FROM customer_activities
    GROUP BY cohort_month, period_number
),
cohort_sizes AS (
    SELECT 
        cohort_month,
        COUNT(DISTINCT customer_id) as total_customers
    FROM first_purchases
    GROUP BY cohort_month
)
SELECT 
    cd.cohort_month,
    cd.period_number,
    cd.customers,
    cs.total_customers,
    ROUND(100.0 * cd.customers / cs.total_customers, 2) as retention_rate
FROM cohort_data cd
JOIN cohort_sizes cs ON cd.cohort_month = cs.cohort_month
ORDER BY cd.cohort_month, cd.period_number;
\`\`\`

## Query Optimization

### Index Strategy
\`\`\`sql
-- Create strategic indexes
CREATE INDEX idx_orders_customer_date ON orders(customer_id, order_date);
CREATE INDEX idx_orders_status_date ON orders(status, order_date) WHERE status IN ('pending', 'processing');
CREATE INDEX idx_products_category_price ON products(category_id, price DESC);

-- Partial indexes for frequently filtered data
CREATE INDEX idx_active_customers ON customers(last_activity_date) 
WHERE status = 'active';

-- Composite indexes for complex queries
CREATE INDEX idx_order_items_lookup ON order_items(order_id, product_id, quantity);
\`\`\`

### Query Performance Analysis
\`\`\`sql
-- Analyze query execution plan
EXPLAIN (ANALYZE, BUFFERS, FORMAT JSON)
SELECT 
    c.name,
    COUNT(o.order_id) as order_count,
    SUM(o.total_amount) as total_spent
FROM customers c
LEFT JOIN orders o ON c.customer_id = o.customer_id
WHERE c.registration_date >= '2024-01-01'
GROUP BY c.customer_id, c.name
HAVING COUNT(o.order_id) > 5
ORDER BY total_spent DESC;

-- Query optimization techniques
-- Before: Inefficient subquery
SELECT * FROM products 
WHERE category_id IN (
    SELECT category_id FROM categories WHERE name LIKE '%Electronics%'
);

-- After: Optimized join
SELECT p.* 
FROM products p
INNER JOIN categories c ON p.category_id = c.category_id
WHERE c.name LIKE '%Electronics%';
\`\`\`

## Advanced Data Analysis Patterns

### Pivot and Unpivot Operations
\`\`\`sql
-- Dynamic pivot for sales by month
SELECT 
    product_name,
    SUM(CASE WHEN EXTRACT(MONTH FROM order_date) = 1 THEN quantity ELSE 0 END) as jan_sales,
    SUM(CASE WHEN EXTRACT(MONTH FROM order_date) = 2 THEN quantity ELSE 0 END) as feb_sales,
    SUM(CASE WHEN EXTRACT(MONTH FROM order_date) = 3 THEN quantity ELSE 0 END) as mar_sales,
    -- ... continue for all months
    SUM(quantity) as total_sales
FROM orders o
JOIN order_items oi ON o.order_id = oi.order_id
JOIN products p ON oi.product_id = p.product_id
WHERE EXTRACT(YEAR FROM order_date) = 2024
GROUP BY product_name;

-- Unpivot example (PostgreSQL)
SELECT 
    customer_id,
    metric_name,
    metric_value
FROM (
    SELECT 
        customer_id,
        total_orders,
        total_spent,
        avg_order_value
    FROM customer_metrics
) cm
CROSS JOIN LATERAL (
    VALUES 
        ('total_orders', cm.total_orders),
        ('total_spent', cm.total_spent),
        ('avg_order_value', cm.avg_order_value)
) as unpivoted(metric_name, metric_value);
\`\`\``,
        practice: {
          task: 'Create a comprehensive sales analytics dashboard using advanced SQL techniques',
          starter: `-- Build queries for:
-- 1. Monthly sales trends with year-over-year comparison
-- 2. Customer segmentation based on purchase behavior
-- 3. Product performance analysis with rankings
-- 4. Cohort retention analysis
-- 5. Sales forecasting using historical data

-- Start with monthly trends
WITH monthly_sales AS (
    SELECT 
        DATE_TRUNC('month', order_date) as month,
        SUM(total_amount) as revenue
    FROM orders
    WHERE order_date >= DATE('2023-01-01')
    GROUP BY DATE_TRUNC('month', order_date)
)
-- Add your analysis here
SELECT * FROM monthly_sales;`,
          hints: [
            'Use window functions for year-over-year comparisons',
            'Implement RFM analysis for customer segmentation',
            'Create running totals and moving averages',
            'Use CTEs for complex multi-step calculations',
            'Apply CASE statements for conditional aggregations',
            'Optimize queries with appropriate indexes'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'advanced-sql-1',
              question: 'Which SQL function is used to access the previous row\'s value in a window?',
              options: ['PREVIOUS()', 'LAG()', 'PRIOR()', 'BACK()'],
              correctAnswer: 1,
              difficulty: 'medium' as const,
              explanation: 'LAG() function accesses data from the previous row in the result set.',
              points: 15
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'database'
  },
  {
    id: 'nosql-databases',
    title: 'NoSQL Database Design',
    description: 'Master MongoDB, Redis, and other NoSQL databases for modern application development.',
    level: 'intermediate',
    totalDuration: '10-12 hours',
    image: '/api/placeholder/400/300',
    prerequisites: ['Database fundamentals', 'JSON understanding', 'Basic programming skills'],
    learningOutcomes: [
      'Design effective NoSQL data models',
      'Master MongoDB operations and aggregation',
      'Implement caching strategies with Redis',
      'Choose appropriate NoSQL database types',
      'Handle scaling and performance optimization',
      'Integrate NoSQL with application architecture'
    ],
    modules: [
      {
        id: 'mongodb-mastery',
        title: 'MongoDB Design & Operations',
        description: 'Advanced MongoDB document modeling, querying, and aggregation pipelines',
        duration: '150 minutes',
        difficulty: 'intermediate',
        objectives: [
          'Design optimal document schemas',
          'Master MongoDB query operations',
          'Build complex aggregation pipelines',
          'Implement indexing strategies'
        ],
        content: `# MongoDB Design & Operations

## Document Modeling Patterns

### Embedding vs. Referencing
\`\`\`javascript
// Embedding pattern - good for one-to-few relationships
const userWithPosts = {
    _id: ObjectId("..."),
    username: "john_doe",
    email: "john@example.com",
    posts: [
        {
            title: "My First Post",
            content: "This is my first blog post...",
            createdAt: new Date(),
            tags: ["javascript", "mongodb"]
        },
        {
            title: "Learning NoSQL",
            content: "NoSQL databases are great for...",
            createdAt: new Date(),
            tags: ["nosql", "database"]
        }
    ]
};

// Referencing pattern - good for one-to-many or many-to-many
const user = {
    _id: ObjectId("507f1f77bcf86cd799439011"),
    username: "john_doe",
    email: "john@example.com"
};

const post = {
    _id: ObjectId("507f1f77bcf86cd799439012"),
    title: "My First Post",
    content: "This is my first blog post...",
    author: ObjectId("507f1f77bcf86cd799439011"), // Reference to user
    createdAt: new Date()
};
\`\`\`

### Advanced Schema Patterns
\`\`\`javascript
// Polymorphic pattern - different document types in same collection
const productCatalog = [
    {
        _id: ObjectId("..."),
        type: "book",
        title: "MongoDB Handbook",
        author: "John Smith",
        isbn: "978-0123456789",
        price: 29.99
    },
    {
        _id: ObjectId("..."),
        type: "electronics",
        name: "Smartphone",
        brand: "TechCorp",
        model: "X1000",
        specifications: {
            screen: "6.1 inch",
            storage: "128GB",
            camera: "12MP"
        },
        price: 699.99
    }
];

// Bucket pattern - time series data
const temperatureReading = {
    _id: ObjectId("..."),
    sensor_id: "sensor_001",
    bucket_date: ISODate("2024-01-01T00:00:00Z"),
    readings: [
        {
            timestamp: ISODate("2024-01-01T00:00:00Z"),
            temperature: 23.5,
            humidity: 45.2
        },
        {
            timestamp: ISODate("2024-01-01T00:05:00Z"),
            temperature: 23.7,
            humidity: 45.8
        }
        // ... more readings in this hour bucket
    ]
};
\`\`\`

## Advanced Querying

### Complex Find Operations
\`\`\`javascript
// Query with multiple conditions
db.products.find({
    $and: [
        { price: { $gte: 10, $lte: 100 } },
        { category: "electronics" },
        { $or: [
            { brand: "Samsung" },
            { brand: "Apple" }
        ]}
    ]
});

// Array queries
db.users.find({
    "interests": { $all: ["javascript", "mongodb"] }, // Has both elements
    "skills.level": { $elemMatch: { $gte: 3 } }      // Array of objects
});

// Text search
db.articles.find({
    $text: { $search: "mongodb database nosql" }
}).sort({ score: { $meta: "textScore" } });

// Geospatial queries
db.restaurants.find({
    location: {
        $near: {
            $geometry: { type: "Point", coordinates: [-73.9667, 40.78] },
            $maxDistance: 1000 // meters
        }
    }
});
\`\`\`

### Aggregation Pipeline
\`\`\`javascript
// Complex aggregation pipeline
db.orders.aggregate([
    // Stage 1: Match recent orders
    {
        $match: {
            orderDate: { $gte: new Date("2024-01-01") }
        }
    },
    
    // Stage 2: Unwind order items
    {
        $unwind: "$items"
    },
    
    // Stage 3: Lookup product details
    {
        $lookup: {
            from: "products",
            localField: "items.productId",
            foreignField: "_id",
            as: "productInfo"
        }
    },
    
    // Stage 4: Add calculated fields
    {
        $addFields: {
            itemTotal: { $multiply: ["$items.quantity", "$items.price"] },
            productName: { $arrayElemAt: ["$productInfo.name", 0] },
            category: { $arrayElemAt: ["$productInfo.category", 0] }
        }
    },
    
    // Stage 5: Group by category
    {
        $group: {
            _id: "$category",
            totalRevenue: { $sum: "$itemTotal" },
            orderCount: { $sum: 1 },
            avgOrderValue: { $avg: "$itemTotal" },
            topProducts: {
                $push: {
                    product: "$productName",
                    revenue: "$itemTotal"
                }
            }
        }
    },
    
    // Stage 6: Sort results
    {
        $sort: { totalRevenue: -1 }
    },
    
    // Stage 7: Limit top categories
    {
        $limit: 10
    }
]);

// Time-based analytics
db.events.aggregate([
    {
        $match: {
            timestamp: { $gte: new Date("2024-01-01") }
        }
    },
    {
        $group: {
            _id: {
                year: { $year: "$timestamp" },
                month: { $month: "$timestamp" },
                day: { $dayOfMonth: "$timestamp" }
            },
            eventCount: { $sum: 1 },
            uniqueUsers: { $addToSet: "$userId" }
        }
    },
    {
        $addFields: {
            uniqueUserCount: { $size: "$uniqueUsers" }
        }
    },
    {
        $project: {
            date: {
                $dateFromParts: {
                    year: "$_id.year",
                    month: "$_id.month",
                    day: "$_id.day"
                }
            },
            eventCount: 1,
            uniqueUserCount: 1
        }
    },
    {
        $sort: { date: 1 }
    }
]);
\`\`\`

## Performance Optimization

### Indexing Strategies
\`\`\`javascript
// Single field index
db.users.createIndex({ email: 1 });

// Compound index
db.orders.createIndex({ customerId: 1, orderDate: -1 });

// Text index for search
db.articles.createIndex({ 
    title: "text", 
    content: "text" 
}, { 
    weights: { title: 10, content: 5 }
});

// Geospatial index
db.restaurants.createIndex({ location: "2dsphere" });

// Partial index
db.products.createIndex(
    { price: 1 },
    { partialFilterExpression: { price: { $gt: 0 } } }
);

// TTL index for automatic document expiration
db.sessions.createIndex(
    { createdAt: 1 },
    { expireAfterSeconds: 3600 } // 1 hour
);
\`\`\`

### Query Optimization
\`\`\`javascript
// Use explain to analyze query performance
db.orders.find({ customerId: "12345" }).explain("executionStats");

// Projection to limit returned fields
db.users.find(
    { status: "active" },
    { name: 1, email: 1, _id: 0 }
);

// Limit and sort optimization
db.products.find({ category: "electronics" })
    .sort({ price: -1 })
    .limit(10);

// Aggregation optimization with $match early
db.orders.aggregate([
    { $match: { status: "completed" } }, // Filter early
    { $lookup: { ... } },                // Then join
    { $group: { ... } }                  // Finally group
]);
\`\`\`

## Redis Integration

### Caching Patterns
\`\`\`javascript
// Cache-aside pattern with MongoDB and Redis
const Redis = require('ioredis');
const { MongoClient } = require('mongodb');

const redis = new Redis(process.env.REDIS_URL);
const mongo = new MongoClient(process.env.MONGODB_URL);

class UserService {
    async getUser(userId) {
        // Try cache first
        const cached = await redis.get(\`user:\${userId}\`);
        if (cached) {
            return JSON.parse(cached);
        }
        
        // Fallback to database
        const user = await mongo.db('app').collection('users')
            .findOne({ _id: ObjectId(userId) });
        
        if (user) {
            // Cache for 1 hour
            await redis.setex(\`user:\${userId}\`, 3600, JSON.stringify(user));
        }
        
        return user;
    }
    
    async updateUser(userId, updates) {
        // Update database
        const result = await mongo.db('app').collection('users')
            .updateOne({ _id: ObjectId(userId) }, { $set: updates });
        
        // Invalidate cache
        await redis.del(\`user:\${userId}\`);
        
        return result;
    }
}
\`\`\``,
        practice: {
          task: 'Design and implement a social media application backend using MongoDB with Redis caching',
          starter: `// Design schemas for:
// 1. Users with profiles and followers
// 2. Posts with comments and likes
// 3. Real-time notifications
// 4. Activity feeds

const userSchema = {
    _id: ObjectId("..."),
    username: "",
    // Add your schema design
};

const postSchema = {
    _id: ObjectId("..."),
    author: ObjectId("..."),
    // Add your schema design
};

// Implement caching strategies
class SocialMediaService {
    async getUserFeed(userId) {
        // Add your implementation
    }
    
    async createPost(userId, postData) {
        // Add your implementation
    }
}`,
          hints: [
            'Design efficient schemas for social graph relationships',
            'Use aggregation pipelines for complex feed generation',
            'Implement Redis caching for frequently accessed data',
            'Create appropriate indexes for query performance',
            'Handle real-time updates with change streams',
            'Design for horizontal scaling'
          ]
        },
        quiz: {
          questions: [
            {
              id: 'mongodb-1',
              question: 'Which MongoDB aggregation stage is used to combine documents from different collections?',
              options: ['$match', '$group', '$lookup', '$project'],
              correctAnswer: 2,
              difficulty: 'medium' as const,
              explanation: '$lookup stage performs a left outer join to another collection in the same database.',
              points: 15
            }
          ],
          passThreshold: 75
        }
      }
    ],
    type: 'database'
  }
];